import React from 'react'
import { Box, Card, CardContent, Typography } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import type { KeyStatSurveyItem } from '../../../../models/Report'
import { SurveyQuestionType } from '../../../../models/Survey'
import { KeyStatType } from '../constants'
import { black, blue } from '../../../../colors'
import StackedChartForMultiChoiceAndMultiSelect from './StackedChartForMultiChoiceAndMultiSelect'
import StatButtonGroup from './StatButtonGroup'
import QuestionSampleSizeRow from '../QuestionSampleSizeRow'
import { getQuestionSampleStats } from '../questionSampleSize'
import { getReportTitleDisplayItems } from '../reportTitles'
import { getModeChartDisplayData } from './MultiChoiceAndMultiSelectChart.helper'

interface MultiChoiceAndMultiSelectChartProps {
  titles?: string[]
  report: KeyStatSurveyItem
  questionTitle: string
}

export const statButtons = [
  { label: 'DIST', value: KeyStatType.DIST },
  { label: 'MODE', value: KeyStatType.MODE }
]

const MultiChoiceAndMultiSelectChart: React.FC<MultiChoiceAndMultiSelectChartProps> = ({
  titles = [],
  report,
  questionTitle
}) => {
  const questionType = report.itemType
  const [selectedStat, setSelectedStat] = React.useState<KeyStatType>(statButtons[0].value)
  const questionSampleStats = getQuestionSampleStats({ report, titles })
  const displayTitles = getReportTitleDisplayItems(titles).map(({ displayTitle }) => displayTitle)
  const modeChartDisplayData = getModeChartDisplayData(report)

  // Only for MultiSelect and MultipleChoice
  if (questionType !== SurveyQuestionType.MultiSelect && questionType !== SurveyQuestionType.MultipleChoice) {
    return null
  }

  const yAxisLabelFormatter = (value: number | string): string | number => {
    let label = String(value)
    if (label.length > 25) {
      label = label.slice(0, 23) + '...'
    }
    return label
  }

  const getChartData = (): any[] => {
    if (selectedStat === KeyStatType.MODE) {
      return modeChartDisplayData.values
    }
    return []
  }

  const chartData = getChartData()

  const option = {
    grid: { left: 180 },
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        if (Array.isArray(params) && params.length > 0) {
          const axisLabel = params[0].axisValueLabel || params[0].name || ''
          const lines = [axisLabel]
          params.forEach((item: any) => {
            const marker = String(item.marker ?? '')
            const value = String(item.value ?? '')
            lines.push(`${marker}${value}`)
          })
          return lines.join('<br/>')
        }
        return ''
      }
    },
    xAxis: {
      type: 'category',
      data: displayTitles,
      nameLocation: 'middle',
      nameGap: 30,
      axisLine: { show: false },
      axisLabel: { fontWeight: 'bold', margin: 20 },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'category',
      data: modeChartDisplayData.optionLabels,
      axisLine: { show: false },
      axisLabel: {
        show: selectedStat === KeyStatType.MODE,
        fontSize: 14,
        fontWeight: 'bold',
        margin: 10,
        width: 400,
        overflow: 'break',
        formatter: yAxisLabelFormatter
      },
      axisTick: { show: false },
      splitLine: { show: true },
      nameGap: 40,
      nameRotate: 0
    },
    series: [
      {
        data: chartData,
        type: 'scatter',
        symbol: 'circle',
        symbolSize: 14,
        itemStyle: {
          color: blue[600]
        }
      }
    ]
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3, mb: 2 }} >
          <Box display="flex" flexDirection="column" gap={1} flex={1} minWidth={0}>
            <Typography variant="h6" color={black[100]} fontWeight="500">
              {questionTitle}
            </Typography>
            <QuestionSampleSizeRow stats={questionSampleStats} />
          </Box>

          <Box flexShrink={0}>
            <StatButtonGroup
              buttons={statButtons}
              selected={selectedStat}
              onSelect={(value) => { setSelectedStat(value as KeyStatType) }}
            />
          </Box>
        </Box>
        {
          selectedStat === KeyStatType.MODE
            ? <ReactECharts
              lazyUpdate
              style={{ width: '100%', height: '400px', maxHeight: 500 }}
              option={option}
            />
            : <StackedChartForMultiChoiceAndMultiSelect
              titles={titles}
              report={report}
            />
        }
      </CardContent>
    </Card>
  )
}

export default MultiChoiceAndMultiSelectChart
